import { useEffect, useRef, useState } from "react";
import {
  playCountdownBeep,
  playShutterClick,
  playStartTone,
} from "./scan-sounds";
import {
  loadBodyPartSettings,
  saveBodyPartSetting,
} from "../../services/scan-storage";

export const MIN_CAPTURES = 25;
const MAX_CAPTURES = 100;
const CAPTURE_INTERVAL_MS = 500;

// Encapsulates a capture run for the scanner: the frame-grabbing interval,
// the optional start-timer countdown, and the optional auto stop at the
// minimum image count.
//
// `captureFrame` grabs the current frame as a JPEG Blob (from useCamera);
// `onFinish` receives the captured frames when a run completes (auto stop,
// image cap, or manual stop).
export function useScanCapture({
  bodyPart,
  captureFrame,
  onFinish,
  defaultStartDelay = 0,
  defaultAutoStop = 0,
}) {
  // capture state
  const [recording, setRecording] = useState(false);
  const [captureCount, setCaptureCount] = useState(0);
  const capturedRef = useRef([]); // captured frames as JPEG Blobs
  const countRef = useRef(0); // latest count, readable inside the timer
  const timerRef = useRef(null);
  const busyRef = useRef(false); // a frame is currently being captured/encoded

  // scan timers — the user's last choices are restored (and kept remembered).
  // start delay in seconds; 0 = no timer. Starts at the user's remembered
  // choice, else defaultStartDelay.
  const [startDelay, setStartDelayState] = useState(() => {
    const saved = loadBodyPartSettings(bodyPart).startDelay;
    return typeof saved === "number" ? saved : defaultStartDelay;
  });
  // auto stop at this image count; 0 = off (stop manually). Starts at the
  // user's remembered choice, else defaultAutoStop.
  const [autoStop, setAutoStopState] = useState(() => {
    const saved = loadBodyPartSettings(bodyPart).autoStop;
    return typeof saved === "number" ? saved : defaultAutoStop;
  });
  const [countdown, setCountdown] = useState(0); // remaining countdown seconds
  const countdownRef = useRef(null);

  function setStartDelay(seconds) {
    saveBodyPartSetting(bodyPart, "startDelay", seconds);
    setStartDelayState(seconds);
  }

  function setAutoStop(count) {
    saveBodyPartSetting(bodyPart, "autoStop", count);
    setAutoStopState(count);
  }

  // keep the latest callbacks readable inside the long-lived interval.
  const captureFrameRef = useRef(captureFrame);
  captureFrameRef.current = captureFrame;
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  // clear the running timers when the consumer unmounts.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, []);

  // Aborting and discarding the ongoing scan
  const abortCapture = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      cancelCountdown();
    }
    busyRef.current = false;
    setRecording(false);
    resetCapture();
  };

  // Stoping the scan for upload
  function stopCapture() {
    // idempotent: a run can only be stopped (and its frames handed over) once.
    if (!timerRef.current) return;
    clearInterval(timerRef.current);
    timerRef.current = null;
    setRecording(false);
    // hand over a snapshot — frames still in flight must not grow the upload.
    onFinishRef.current(capturedRef.current.slice());
  }

  // capture a frame and throw it away, to keep the pipeline warm.
  function warmUpCapture() {
    Promise.resolve(captureFrameRef.current()).catch(() => {});
  }

  function startCapture() {
    // never run two capture intervals at once (e.g. a double-tap).
    if (timerRef.current) return;
    playStartTone();
    warmUpCapture();
    // start a fresh capture run
    capturedRef.current = [];
    countRef.current = 0;
    busyRef.current = false;
    setCaptureCount(0);
    setRecording(true);
    // with auto stop set, end the scan as soon as its image count is reached.
    const stopAt = autoStop || MAX_CAPTURES;
    const timer = setInterval(async () => {
      // skip this tick while the previous frame is still encoding, so slow
      // captures don't pile up and then resolve in a burst.
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const blob = await captureFrameRef.current();
        // drop frames that finish after this run was stopped
        if (timerRef.current !== timer || !blob) return;
        capturedRef.current.push(blob);
        countRef.current += 1;
        setCaptureCount(countRef.current);
        playShutterClick();
        if (countRef.current >= stopAt) {
          stopCapture();
        }
      } finally {
        busyRef.current = false;
      }
    }, CAPTURE_INTERVAL_MS);
    timerRef.current = timer;
  }

  function cancelCountdown() {
    clearInterval(countdownRef.current);
    countdownRef.current = null;
    setCountdown(0);
  }

  // count down the start timer on screen (with a beep every second), then
  // begin the capture.
  function beginCountdown() {
    if (countdownRef.current || timerRef.current) return; // already running
    let remaining = startDelay;
    setCountdown(remaining);
    playCountdownBeep();
    warmUpCapture();
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        cancelCountdown();
        startCapture();
      } else {
        playCountdownBeep();
        // keep the capture pipeline warm through the whole countdown
        warmUpCapture();
      }
    }, 1000);
  }

  function toggleCapture() {
    if (countdownRef.current) {
      // tapped during the start countdown — cancel it
      cancelCountdown();
    } else if (!timerRef.current) {
      if (startDelay > 0) {
        beginCountdown();
      } else {
        startCapture();
      }
    } else if (countRef.current >= MIN_CAPTURES) {
      // can only stop once the minimum has been captured
      stopCapture();
    }
  }

  // discard the captured frames (for a fresh scan).
  function resetCapture() {
    capturedRef.current = [];
    countRef.current = 0;
    setCaptureCount(0);
  }

  // the capture can only be stopped once the minimum frame count is reached.
  const canStop = recording && captureCount >= MIN_CAPTURES;

  return {
    recording,
    captureCount,
    countdown,
    canStop,
    startDelay,
    setStartDelay,
    autoStop,
    setAutoStop,
    toggleCapture,
    abortCapture,
    resetCapture,
  };
}
